import React from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { Eye, EyeOff } from 'lucide-react';

import {
  Modal,
  Button,
  Input,
  Checkbox,
  MultiSelect,
  Loading,
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '../../components/ui';
import { createUserSchema, type CreateUserFormData } from '../../shared/lib/validation-schemas';
import { useCreateUser } from '../../shared/lib/hooks/use-users';
import { useRoles } from '../../shared/lib/hooks/use-roles';
import { isApiError } from '../../shared/api';
import type { MultiSelectOption } from '../../components/ui/multi-select';
import type { ValidationError, Role } from '../../shared/types';

interface CreateUserFormProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const CreateUserForm: React.FC<CreateUserFormProps> = ({
  open,
  onOpenChange,
}) => {
  const { t } = useTranslation();
  const [showPassword, setShowPassword] = React.useState(false);

  // Fetch roles for selection
  const { data: rolesData, isLoading: rolesLoading } = useRoles();

  // Create user mutation
  const createUserMutation = useCreateUser();

  // Form setup
  const form = useForm<CreateUserFormData>({
    resolver: zodResolver(createUserSchema),
    defaultValues: {
      email: '',
      password: '',
      firstName: '',
      lastName: '',
      isActive: true,
      roleIds: [],
    },
  });

  // Convert roles to multi-select options
  const roleOptions: MultiSelectOption[] = React.useMemo(() => {
    if (!rolesData?.items) return [];
    
    return rolesData.items.map((role: Role) => ({
      value: role.id,
      label: role.name,
      disabled: false,
    }));
  }, [rolesData]);

  // Handle form submission
  const onSubmit = async (data: CreateUserFormData) => {
    try {
      await createUserMutation.mutateAsync({
        email: data.email,
        password: data.password,
        firstName: data.firstName || undefined,
        lastName: data.lastName || undefined,
        isActive: data.isActive,
        roleIds: data.roleIds && data.roleIds.length > 0 ? data.roleIds : undefined,
      });

      // Reset form and close modal on success
      form.reset();
      onOpenChange(false);
    } catch (error) {
      console.error('Create user error:', error);
      
      if (isApiError(error)) {
        if (
          error.isValidationError &&
          error.response &&
          typeof error.response === 'object' &&
          'data' in error.response &&
          error.response.data &&
          typeof error.response.data === 'object' &&
          'detail' in error.response.data &&
          Array.isArray(error.response.data.detail)
        ) {
          const validationErrors = error.response.data.detail as ValidationError[];
          
          // Set field-specific errors
          validationErrors.forEach((validationError) => {
            const fieldName = validationError.loc[validationError.loc.length - 1] as string;
            
            // Map backend field names to form field names
            const fieldMapping: Record<string, keyof CreateUserFormData> = {
              'email': 'email',
              'password': 'password',
              'first_name': 'firstName',
              'last_name': 'lastName',
              'is_active': 'isActive',
              'role_ids': 'roleIds',
            };
            
            const formFieldName = fieldMapping[fieldName] || fieldName as keyof CreateUserFormData;
            
            if (formFieldName && formFieldName in form.getValues()) {
              form.setError(formFieldName, {
                type: 'manual',
                message: validationError.msg,
              });
            } else {
              // If we can't map the field, show as root error
              form.setError('root', {
                type: 'manual',
                message: `${fieldName}: ${validationError.msg}`,
              });
            }
          });
        } else if (error.isValidationError) {
          // Fallback for validation errors without detailed info
          form.setError('root', {
            type: 'manual',
            message: error.message,
          });
        } else if (error.status === 409) {
          // Email already exists
          form.setError('email', {
            type: 'manual',
            message: t('errors.emailExists', 'Email already exists'),
          });
        } else {
          form.setError('root', {
            type: 'manual',
            message: t('errors.api', { message: error.message }),
          });
        }
      } else {
        form.setError('root', {
          type: 'manual',
          message: t('errors.unknown'),
        });
      }
    }
  };

  // Handle modal close
  const handleClose = () => {
    if (!form.formState.isSubmitting) {
      form.reset();
      onOpenChange(false);
    }
  };

  const isFormLoading = form.formState.isSubmitting || createUserMutation.isPending;

  return (
    <Modal
      open={open}
      onOpenChange={handleClose}
      title={t('users.createUser')}
      size="lg"
      className="max-w-2xl w-full mx-4 sm:mx-auto max-h-[90vh] overflow-y-auto"
    >
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
          {/* Email Field */}
          <FormField
            control={form.control}
            name="email"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('auth.email')} *</FormLabel>
                <FormControl>
                  <Input
                    type="email"
                    placeholder={t('auth.email')}
                    disabled={isFormLoading}
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* Password Field */}
          <FormField
            control={form.control}
            name="password"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('auth.password')} *</FormLabel>
                <FormControl>
                  <div className="relative">
                    <Input
                      type={showPassword ? 'text' : 'password'}
                      placeholder={t('auth.password')}
                      disabled={isFormLoading}
                      className="pr-10"
                      {...field}
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                      onClick={() => setShowPassword(!showPassword)}
                      disabled={isFormLoading}
                    >
                      {showPassword ? (
                        <EyeOff className="h-4 w-4" />
                      ) : (
                        <Eye className="h-4 w-4" />
                      )}
                      <span className="sr-only">
                        {showPassword ? 'Hide password' : 'Show password'}
                      </span>
                    </Button>
                  </div>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* First Name and Last Name */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <FormField
              control={form.control}
              name="firstName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('users.firstName')}</FormLabel>
                  <FormControl>
                    <Input
                      type="text"
                      placeholder={t('users.firstName')}
                      disabled={isFormLoading}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="lastName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('users.lastName')}</FormLabel>
                  <FormControl>
                    <Input
                      type="text"
                      placeholder={t('users.lastName')}
                      disabled={isFormLoading}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          {/* Roles Selection */}
          <FormField
            control={form.control}
            name="roleIds"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('users.roles')}</FormLabel>
                <FormControl>
                  {rolesLoading ? (
                    <div className="flex items-center justify-center py-4">
                      <Loading size="sm" text={t('common.loading')} />
                    </div>
                  ) : (
                    <MultiSelect
                      options={roleOptions}
                      value={field.value || []}
                      onChange={field.onChange}
                      placeholder={t('roles.selectRoles', 'Select roles...')}
                      disabled={isFormLoading}
                      maxDisplayItems={3}
                    />
                  )}
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* Active Status */}
          <FormField
            control={form.control}
            name="isActive"
            render={({ field }) => (
              <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                <FormControl>
                  <Checkbox
                    checked={field.value}
                    onCheckedChange={field.onChange}
                    disabled={isFormLoading}
                  />
                </FormControl>
                <div className="space-y-1 leading-none">
                  <FormLabel>{t('users.isActive')}</FormLabel>
                  <FormDescription>
                    {t('users.isActiveDescription', 'User can log in and access the system')}
                  </FormDescription>
                </div>
              </FormItem>
            )}
          />

          {/* Form-level Error Display */}
          {form.formState.errors.root && (
            <div className="rounded-md bg-destructive/15 p-3">
              <p className="text-sm text-destructive">{form.formState.errors.root.message}</p>
            </div>
          )}

          {/* Form Actions */}
          <div className="flex flex-col sm:flex-row justify-end gap-3 pt-4 border-t">
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={isFormLoading}
              className="w-full sm:w-auto order-2 sm:order-1"
            >
              {t('common.cancel')}
            </Button>
            <Button
              type="submit"
              disabled={isFormLoading}
              className="w-full sm:w-auto order-1 sm:order-2"
            >
              {isFormLoading ? (
                <div className="flex items-center justify-center">
                  <Loading size="sm" className="mr-2" text="" />
                  {t('common.loading')}
                </div>
              ) : (
                t('common.create')
              )}
            </Button>
          </div>
        </form>
      </Form>
    </Modal>
  );
};